import React, { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import Alert from '@mui/material/Alert'
import {
  Save as SaveIcon,
  Visibility as ViewIcon,
  Sync as SyncIcon
} from '@mui/icons-material'
import { PageLayout, PageHeader } from '../../components'
import { ResponsiveTable, TableEmptyState } from '../../components/TableComponents'
import { StatusChip, SearchInput } from '../../components/atoms'
import { useFetch, useMutation } from '../../hooks'
import { authenticatedFetch } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import { formatNumber } from '../../services/format'
import styles from './AdminCampaignManagement.module.scss'

const getUsageStatus = (campaign) => {
  if (!campaign.plan_minutes || campaign.plan_minutes === 0) {
    return { label: 'No Plan', color: 'neutral' }
  }

  const usage = campaign.current_month_minutes || 0
  const percentage = (usage / campaign.plan_minutes) * 100

  if (percentage >= 100) {
    return { label: 'Exceeded', color: 'error' }
  } else if (percentage >= 75) {
    return { label: 'High Usage', color: 'warning' }
  } else if (percentage >= 50) {
    return { label: 'Moderate', color: 'neutral' }
  } else {
    return { label: 'Low Usage', color: 'success' }
  }
}

const AdminCampaignManagement = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [saving, setSaving] = useState({})
  const [pendingChanges, setPendingChanges] = useState({})
  const [message, setMessage] = useState(null)
  const [showMissingOnly, setShowMissingOnly] = useState(false)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const [sortField, setSortField] = useState('campaign_name')
  const [sortDirection, setSortDirection] = useState('asc')

  const {
    data: campaigns,
    loading,
    setData: setCampaigns,
    refetch: loadCampaigns
  } = useFetch(apiEndpoints.campaigns, {
    initialData: [],
    transform: (data) => Array.isArray(data) ? data : (data.data || []),
    errorMessage: 'Failed to load campaigns'
  })

  const { mutate: syncData, loading: syncing } = useMutation(apiEndpoints.campaignsSyncData, {
    method: 'POST',
    onSuccess: async (result) => {
      await loadCampaigns()
      setMessage({
        type: 'success',
        text: `Sync completed successfully! ${result?.recordsProcessed ?? 0} calls processed, ${result?.campaignsSynced ?? 0} campaigns updated`
      })
    },
    onError: (error) => {
      setMessage({ type: 'error', text: `Sync failed: ${error.message}` })
    }
  })

  const filteredCampaigns = useMemo(() => {
    let filtered = campaigns

    if (searchTerm) {
      filtered = filtered.filter(campaign =>
        campaign.campaign_name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (showMissingOnly) {
      filtered = filtered.filter(campaign =>
        !campaign.plan_minutes || campaign.plan_minutes === 0
      )
    }

    return [...filtered].sort((a, b) => {
      const dir = sortDirection === 'asc' ? 1 : -1
      if (sortField === 'campaign_name') {
        return dir * (a.campaign_name || '').localeCompare(b.campaign_name || '')
      }
      if (sortField === 'plan_minutes') {
        return dir * ((Number(a.plan_minutes) || 0) - (Number(b.plan_minutes) || 0))
      }
      if (sortField === 'current_usage') {
        return dir * ((Number(a.current_month_minutes) || 0) - (Number(b.current_month_minutes) || 0))
      }
      if (sortField === 'status') {
        const aPlan = Number(a.plan_minutes) || 0
        const bPlan = Number(b.plan_minutes) || 0
        const aPercent = aPlan > 0 ? (Number(a.current_month_minutes) || 0) / aPlan : -1
        const bPercent = bPlan > 0 ? (Number(b.current_month_minutes) || 0) / bPlan : -1
        return dir * (aPercent - bPercent)
      }
      return 0
    })
  }, [campaigns, searchTerm, showMissingOnly, sortField, sortDirection])

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const handlePlanMinutesChange = (campaignName, value) => {
    const numericValue = value === '' ? 0 : parseInt(value) || 0
    setPendingChanges(prev => ({
      ...prev,
      [campaignName]: numericValue
    }))
  }

  const savePlanMinutes = async (campaignName) => {
    const newValue = pendingChanges[campaignName]
    if (newValue === undefined) return

    try {
      setSaving(prev => ({ ...prev, [campaignName]: true }))

      const response = await authenticatedFetch(
        apiEndpoints.campaignPlanMinutes(campaignName),
        {
          method: 'PUT',
          body: JSON.stringify({ planMinutes: newValue })
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to update plan minutes')
      }

      setCampaigns(prev => prev.map(campaign =>
        campaign.campaign_name === campaignName
          ? { ...campaign, plan_minutes: newValue }
          : campaign
      ))

      setPendingChanges(prev => {
        const updated = { ...prev }
        delete updated[campaignName]
        return updated
      })

      setMessage({
        type: 'success',
        text: `Updated ${campaignName} plan minutes to ${formatNumber(newValue)}`
      })
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Failed to update ${campaignName}: ${error.message}`
      })
    } finally {
      setSaving(prev => ({ ...prev, [campaignName]: false }))
    }
  }

  const viewCampaignDetails = (campaign) => {
    setSelectedCampaign(campaign)
    setViewDialogOpen(true)
  }

  const handleSyncClick = () => {
    setMessage({ type: 'info', text: 'Syncing recent calls and plan minutes... This may take up to 30 seconds.' })
    syncData()
  }

  const getUsagePercent = (campaign) => {
    if (!campaign.plan_minutes || campaign.plan_minutes === 0) return 0
    return Math.round((campaign.current_month_minutes || 0) / campaign.plan_minutes * 100)
  }

  return (
    <PageLayout>
      <PageHeader
        title='Campaign Management'
        subtitle='Manage campaign plan minutes and monitor usage. Use "Sync Data" to refresh usage from Five9 and plan minutes from HubSpot.'
      />

      {message && (
        <Alert
          severity={message.type}
          onClose={() => setMessage(null)}
          className={styles.alert}
        >
          {message.text}
        </Alert>
      )}

      <div className={styles.toolbar}>
        <div className={styles.toolbarSearch}>
          <SearchInput
            placeholder='Search campaigns...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size='small'
          />
        </div>

        <Button
          variant='outlined'
          className={`${styles.btnOutlined} ${showMissingOnly ? styles.btnOutlinedActive : ''}`}
          onClick={() => setShowMissingOnly(!showMissingOnly)}
        >
          {showMissingOnly ? 'Show all' : 'Show missing plans only'}
        </Button>

        <Button
          variant='contained'
          className={styles.btnSync}
          onClick={handleSyncClick}
          disabled={syncing}
          startIcon={<SyncIcon />}
        >
          {syncing ? 'Syncing...' : 'Sync Data'}
        </Button>

        <span className={styles.campaignCount}>
          <strong>{filteredCampaigns.length}</strong> of {campaigns.length} campaigns
        </span>
      </div>

      <ResponsiveTable
        columns={[
          {
            id: 'campaign_name',
            label: 'Campaign Name',
            sortable: true,
            renderCell: (row) => (
              <Typography variant='body2'>
                {row.campaign_name}
              </Typography>
            )
          },
          {
            id: 'plan_minutes',
            label: 'Plan Minutes',
            align: 'right',
            sortable: true,
            renderCell: (row) => {
              const hasPendingChange = pendingChanges[row.campaign_name] !== undefined
              const displayValue = hasPendingChange
                ? pendingChanges[row.campaign_name]
                : row.plan_minutes || 0
              return (
                <TextField
                  type='number'
                  value={displayValue}
                  onChange={(e) => handlePlanMinutesChange(row.campaign_name, e.target.value)}
                  inputProps={{ min: 0, 'aria-label': `Plan minutes for ${row.campaign_name}` }}
                  size='small'
                  style={{ width: 100 }}
                  className={styles.planInput}
                />
              )
            }
          },
          {
            id: 'current_usage',
            label: 'Current Usage',
            align: 'right',
            sortable: true,
            renderCell: (row) => (
              <>
                <Typography variant='body2' style={{ fontWeight: 600 }}>
                  {formatNumber(row.current_month_minutes || 0)} min
                </Typography>
                {row.plan_minutes > 0 && (
                  <Typography variant='caption' color='textSecondary'>
                    ({getUsagePercent(row)}%)
                  </Typography>
                )}
              </>
            )
          },
          {
            id: 'status',
            label: 'Status',
            align: 'center',
            sortable: true,
            renderCell: (row) => {
              const status = getUsageStatus(row)
              return (
                <StatusChip
                  label={status.label}
                  color={status.color}
                  size='small'
                />
              )
            }
          },
          {
            id: 'actions',
            label: 'Actions',
            align: 'center',
            renderCell: (row) => {
              const hasPendingChange = pendingChanges[row.campaign_name] !== undefined
              return (
                <Box display='flex' justifyContent='center' style={{ gap: 8 }}>
                  {hasPendingChange && (
                    <Button
                      size='small'
                      variant='contained'
                      className={styles.btnSave}
                      onClick={() => savePlanMinutes(row.campaign_name)}
                      disabled={saving[row.campaign_name]}
                      startIcon={<SaveIcon />}
                    >
                      {saving[row.campaign_name] ? 'Saving...' : 'Save'}
                    </Button>
                  )}
                  <IconButton
                    size='small'
                    onClick={() => viewCampaignDetails(row)}
                    className={styles.actionButton}
                    title='View detailed statistics'
                  >
                    <ViewIcon fontSize='small' />
                  </IconButton>
                </Box>
              )
            }
          }
        ]}
        data={filteredCampaigns}
        loading={loading}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        variant='campaigns'
        tableProps={{ 'aria-label': 'Campaign management table' }}
        emptyState={
          <TableEmptyState
            searched={!!searchTerm}
            title={searchTerm ? 'No matching campaigns' : 'No campaigns found'}
            message={searchTerm ? 'Try adjusting your search term' : 'Campaigns will appear here once data is synced from Five9'}
            icon={searchTerm ? 'search' : 'bullhorn'}
          />
        }
      />

      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth='sm'
        fullWidth
        className={styles.dialog}
      >
        <DialogTitle className={styles.dialogTitle}>Campaign Details</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          {selectedCampaign && (
            <>
              <Typography variant='h6' className={styles.dialogCampaignName}>
                {selectedCampaign.campaign_name}
              </Typography>

              <div className={styles.dialogGrid}>
                <div className={styles.dialogField}>
                  <span className={styles.dialogLabel}>Plan Minutes</span>
                  <span className={styles.dialogValue}>
                    {formatNumber(selectedCampaign.plan_minutes || 0)}
                  </span>
                </div>

                <div className={styles.dialogField}>
                  <span className={styles.dialogLabel}>Current Month Usage</span>
                  <span className={styles.dialogValue}>
                    {formatNumber(selectedCampaign.current_month_minutes || 0)} min
                  </span>
                </div>

                <div className={styles.dialogField}>
                  <span className={styles.dialogLabel}>Usage Percentage</span>
                  <span className={styles.dialogValue}>
                    {getUsagePercent(selectedCampaign)}%
                  </span>
                </div>

                <div className={styles.dialogField}>
                  <span className={styles.dialogLabel}>Remaining</span>
                  <span className={styles.dialogValue}>
                    {formatNumber(Math.max(0, (selectedCampaign.plan_minutes || 0) - (selectedCampaign.current_month_minutes || 0)))} min
                  </span>
                </div>
              </div>

              <div className={styles.dialogStatusRow}>
                <span className={styles.dialogLabel}>Status</span>
                <StatusChip
                  label={getUsageStatus(selectedCampaign).label}
                  color={getUsageStatus(selectedCampaign).color}
                  size='small'
                />
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)} className={styles.dialogCloseBtn}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  )
}

export default AdminCampaignManagement
